import React from 'react';
import {
  clearScanCountLines,
  initialCounts,
  parseScanCountDraft,
  restoreScanCountDraft,
  serializeScanCountDraft,
  stampScanCounts,
  staleScanCountLineIds,
  updateScanCountDraft,
  type ScanCountCounts,
  type ScanCountLine,
  type ScanCountStamps,
} from '@/domain/warehouse';

/**
 * What the dialog says about the count: it came back (`restored`, some lines possibly left out),
 * none of it came back (`stale`), or counts were cleared while the dialog was open (`changed`).
 * `lineNames` are the lines whose count was cleared because units were done on them since.
 */
export type ScanCountDraftNotice = { kind: 'restored' | 'stale' | 'changed'; savedAt: string; lineNames: string[] };

export type ScanCountDraftState = {
  counts: ScanCountCounts;
  /** The latest counts, for async work (lookups, submits) that outlives a render. */
  countsRef: React.MutableRefObject<ScanCountCounts>;
  setCounts: (next: ScanCountCounts) => void;
  notice: ScanCountDraftNotice | null;
  /** Forgets the stored counts of the lines shown (everything applied) and empties them; other lines keep theirs. */
  discard: () => void;
};

// Storage can be missing, blocked or full (private windows, previews). The count still works
// without it; it is just not kept across a close.
function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Not kept: see above.
  }
}

function removeStorage(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing to remove.
  }
}

function lineNames(lines: readonly ScanCountLine[], lineIds: readonly string[]): string[] {
  return lines.filter((line) => lineIds.includes(line.id)).map((line) => line.name || line.sku || 'A line');
}

/** Writes the counts of the lines shown, keeping what the stored draft holds for other lines. */
function persist(storageKey: string | null, lines: readonly ScanCountLine[], counts: ScanCountCounts, stamps: ScanCountStamps): void {
  if (!storageKey) return;
  const raw = readStorage(storageKey);
  const next = updateScanCountDraft(parseScanCountDraft(raw), lines, counts, stamps);
  if (next) writeStorage(storageKey, serializeScanCountDraft(next));
  else if (raw !== null) removeStorage(storageKey);
}

type Loaded = { counts: ScanCountCounts; stamps: ScanCountStamps; notice: ScanCountDraftNotice | null };

function load(storageKey: string | null, lines: readonly ScanCountLine[]): Loaded {
  if (!storageKey) return { counts: initialCounts(lines), stamps: {}, notice: null };
  const raw = readStorage(storageKey);
  const draft = parseScanCountDraft(raw);
  if (raw !== null && !draft) removeStorage(storageKey);
  const restored = restoreScanCountDraft(draft, lines);
  // Stale lines leave the store now; the other lines' counts stay as they were.
  if (restored.staleLineIds.length) persist(storageKey, lines, restored.counts, restored.stamps);
  const savedAt = draft?.savedAt ?? '';
  const cleared = lineNames(lines, restored.staleLineIds);
  let notice: ScanCountDraftNotice | null = null;
  if (restored.restoredLineIds.length) notice = { kind: 'restored', savedAt, lineNames: cleared };
  else if (cleared.length) notice = { kind: 'stale', savedAt, lineNames: cleared };
  return { counts: restored.counts, stamps: restored.stamps, notice };
}

/**
 * The dialog's counts, kept per viewer in localStorage under `storageKey` when one is given, line by
 * line: a dialog for some of the lines (Receive selected) keeps the other lines' counts. Mount it
 * with the open dialog. The draft is read once the lines arrive (an empty list waits, so a dialog
 * opened while its lines load cannot discard it). A quantity line's count is kept only while nothing
 * is done on that line since it was counted: when the lines refresh (a receipt here, someone else's,
 * or one whose answer was lost) or the draft is read again, such a count is cleared and the notice
 * says so. Every change is written at once, so closing keeps the count without a save step, even
 * when a submit settles after the dialog closed.
 */
export function useScanCountDraft(lines: readonly ScanCountLine[], storageKey: string | null): ScanCountDraftState {
  const linesRef = React.useRef(lines);
  linesRef.current = lines;
  const ready = lines.length > 0;
  const [initial] = React.useState(() => (ready ? load(storageKey, lines) : null));
  const [counts, setCountsState] = React.useState<ScanCountCounts>(() => initial?.counts ?? {});
  const [notice, setNotice] = React.useState<ScanCountDraftNotice | null>(() => initial?.notice ?? null);
  const loadedRef = React.useRef(initial !== null);
  const countsRef = React.useRef(counts);
  const stampsRef = React.useRef<ScanCountStamps>(initial?.stamps ?? {});
  const linesKey = lines.map((line) => `${line.id}:${line.alreadyDone}`).join('|');
  const seenLinesKeyRef = React.useRef(linesKey);

  React.useEffect(() => {
    if (loadedRef.current || !ready) return;
    const loaded = load(storageKey, linesRef.current);
    loadedRef.current = true;
    seenLinesKeyRef.current = linesKey;
    countsRef.current = loaded.counts;
    stampsRef.current = loaded.stamps;
    setCountsState(loaded.counts);
    setNotice(loaded.notice);
  }, [ready, storageKey, linesKey]);

  // The lines moved on while the dialog is open. A counted quantity line with units done on it since
  // its count began is cleared, exactly as a stored draft would be when read again.
  React.useEffect(() => {
    if (!loadedRef.current || seenLinesKeyRef.current === linesKey) return;
    seenLinesKeyRef.current = linesKey;
    const current = linesRef.current;
    const stale = staleScanCountLineIds(current, countsRef.current, stampsRef.current);
    if (!stale.length) return;
    const next = clearScanCountLines(current, countsRef.current, stale);
    countsRef.current = next;
    stampsRef.current = stampScanCounts(current, next, stampsRef.current);
    setCountsState(next);
    setNotice({ kind: 'changed', savedAt: '', lineNames: lineNames(current, stale) });
    persist(storageKey, current, next, stampsRef.current);
  }, [linesKey, storageKey]);

  const setCounts = React.useCallback(
    function setCounts(next: ScanCountCounts) {
      if (next === countsRef.current) return;
      countsRef.current = next;
      stampsRef.current = stampScanCounts(linesRef.current, next, stampsRef.current);
      setCountsState(next);
      if (loadedRef.current) persist(storageKey, linesRef.current, next, stampsRef.current);
    },
    [storageKey],
  );

  const discard = React.useCallback(
    function discard() {
      const empty = initialCounts(linesRef.current);
      countsRef.current = empty;
      stampsRef.current = {};
      setCountsState(empty);
      persist(storageKey, linesRef.current, empty, {});
    },
    [storageKey],
  );

  return { counts, countsRef, setCounts, notice, discard };
}
